import React from 'react';
import {
  Box,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  useColorModeValue,
  Icon,
  Divider,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Select,
} from '@chakra-ui/react';
import { Settings, Brain, Zap, GitBranch, Activity } from 'lucide-react';

const SystemConfigPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  const configs = [
    {
      section: "Model Configuration",
      icon: Brain,
      settings: [
        { name: "Auto-Training", type: "switch", value: true },
        { name: "Training Frequency", type: "select", value: "daily", options: ["hourly", "daily", "weekly"] },
        { name: "Confidence Threshold", type: "slider", value: 0.85 },
      ]
    },
    {
      section: "Pipeline Settings",
      icon: Activity,
      settings: [
        { name: "Real-time Processing", type: "switch", value: true },
        { name: "Batch Size", type: "select", value: "medium", options: ["small", "medium", "large"] },
        { name: "Queue Priority", type: "slider", value: 0.75 },
      ]
    },
    {
      section: "System Resources",
      icon: Zap,
      settings: [
        { name: "GPU Acceleration", type: "switch", value: true },
        { name: "Memory Allocation", type: "slider", value: 0.6 },
        { name: "Thread Count", type: "select", value: "auto", options: ["auto", "low", "medium", "high"] },
      ]
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {configs.map((config, idx) => (
        <Box
          key={idx}
          bg={bgColor}
          p={6}
          borderRadius="lg"
          boxShadow="sm"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <HStack mb={4}>
            <Icon as={config.icon} boxSize={5} color="methodist.blue" />
            <Text fontSize="lg" fontWeight="medium">{config.section}</Text>
          </HStack>
          <VStack spacing={4} align="stretch">
            {config.settings.map((setting, index) => (
              <HStack key={index} justify="space-between">
                <Text fontSize="sm">{setting.name}</Text>
                {setting.type === 'switch' && (
                  <Switch colorScheme="purple" isChecked={setting.value as boolean} />
                )}
                {setting.type === 'slider' && (
                  <Box w="200px">
                    <Slider defaultValue={setting.value as number} min={0} max={1} step={0.01}>
                      <SliderTrack>
                        <SliderFilledTrack bg="methodist.blue" />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                  </Box>
                )}
                {setting.type === 'select' && (
                  <Select w="200px" size="sm" value={setting.value as string}>
                    {(setting.options as string[]).map(opt => (
                      <option key={opt} value={opt}>
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </option>
                    ))}
                  </Select>
                )}
              </HStack>
            ))}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
};

export default SystemConfigPanel;
